import React, { useState, useRef, useCallback, useEffect } from 'react';
import * as Icons from 'react-feather';
import { toast } from 'react-toastify';
import Skeleton from 'react-loading-skeleton';
import { Api } from '../../../../services/Api';

const ACCEPTED_TYPES = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'];
const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100 Mo
const ITEMS_PER_PAGE = 20;

const AnnonceList = () => {
    const [annonces, setAnnonces] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [nextPageUrl, setNextPageUrl] = useState(null);
    const [previousPageUrl, setPreviousPageUrl] = useState(null);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [isDragOver, setIsDragOver] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [selectedAnnonce, setSelectedAnnonce] = useState(null);
    const [taskStatus, setTaskStatus] = useState(null); // null | { taskId, status, message }
    const fileInputRef = useRef(null);
    const pollingRef = useRef(null);

    const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE);

    const fetchAnnonces = useCallback(async (endpoint) => {
        setLoading(true);
        try {
            const url = endpoint || 'formalite/annonces-v2/';
            const response = await Api.get(url);
            setAnnonces(response.results || []);
            setTotalCount(response.count || 0);
            setNextPageUrl(response.next || null);
            setPreviousPageUrl(response.previous || null);
        } catch (error) {
            console.error('Erreur lors du chargement des annonces:', error);
            toast.error("Erreur lors du chargement des annonces");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchAnnonces();
    }, [fetchAnnonces]);

    const handlePageChange = (url, newPage) => {
        if (!url) return;
        const path = url.replace(/^https?:\/\/[^/]+\//, '');
        setPage(newPage);
        fetchAnnonces(path);
    };

    const validateFile = (file) => {
        if (!ACCEPTED_TYPES.includes(file.type)) {
            return 'Format non accepté : ' + file.name;
        }
        if (file.size > MAX_FILE_SIZE) {
            return 'Fichier trop volumineux : ' + file.name + ' (max 100 Mo)';
        }
        return null;
    };

    const stopPolling = useCallback(() => {
        if (pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
        }
    }, []);

    const checkTaskStatus = useCallback(async (taskId) => {
        try {
            var result = await Api.get('formalite/annonces-v2/task-status/' + taskId + '/');
            var status = result.status || result.state;

            if (status === 'SUCCESS') {
                stopPolling();
                var nbAnnonces = result.result && result.result.annonce_ids ? result.result.annonce_ids.length : 0;
                var successMsg = nbAnnonces > 0
                    ? nbAnnonces + " annonce(s) extraite(s) avec succès"
                    : "Traitement terminé avec succès";
                setTaskStatus({ taskId: taskId, status: 'SUCCESS', message: successMsg });
                setPage(1);
                fetchAnnonces();
            } else if (status === 'FAILURE') {
                stopPolling();
                var errorMsg = result.error || "Le traitement a échoué";
                setTaskStatus({ taskId: taskId, status: 'FAILURE', message: errorMsg });
            } else {
                setTaskStatus({ taskId: taskId, status: status, message: getTaskStatusMessage(status) });
            }
        } catch (err) {
            console.error('Erreur lors de la vérification du statut:', err);
            stopPolling();
            setTaskStatus(null);
            toast.error("Erreur lors de la vérification du statut du traitement");
        }
    }, [stopPolling, fetchAnnonces]);

    const startPolling = useCallback((taskId) => {
        stopPolling();
        setTaskStatus({ taskId: taskId, status: 'PENDING', message: 'Traitement en cours...' });

        // Premier check immédiat après 5s
        setTimeout(function() {
            checkTaskStatus(taskId);
        }, 5000);

        // Puis toutes les 60s
        pollingRef.current = setInterval(function() {
            checkTaskStatus(taskId);
        }, 60000);
    }, [stopPolling, checkTaskStatus]);

    useEffect(function() {
        return function() {
            if (pollingRef.current) {
                clearInterval(pollingRef.current);
            }
        };
    }, []);

    const handleFiles = useCallback(async (files) => {
        var validFiles = [];
        var errors = [];

        for (var i = 0; i < files.length; i++) {
            var error = validateFile(files[i]);
            if (error) {
                errors.push(error);
            } else {
                validFiles.push(files[i]);
            }
        }

        if (errors.length > 0) {
            toast.error(errors.join('\n'));
        }

        if (validFiles.length === 0) return;

        setUploading(true);

        try {
            var formData = new FormData();
            for (var j = 0; j < validFiles.length; j++) {
                formData.append('fichiers', validFiles[j]);
            }

            var response = await Api.postFormData(
                'formalite/annonces-v2/insert-annonce/',
                formData
            );

            if (response.task_id) {
                toast.info(response.message || "Fichier(s) en cours de traitement");
                startPolling(response.task_id);
            } else {
                toast.success(response.message || "Annonce(s) extraite(s) avec succès");
                setPage(1);
                await fetchAnnonces();
            }
        } catch (err) {
            console.error('Erreur lors du téléversement:', err);
            toast.error("Erreur lors du téléversement des fichiers");
        } finally {
            setUploading(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    }, [fetchAnnonces, startPolling]);

    const handleDragOver = (e) => {
        e.preventDefault();
        setIsDragOver(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        setIsDragOver(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragOver(false);
        handleFiles(e.dataTransfer.files);
    };

    const handleFileSelect = (e) => {
        if (e.target.files.length > 0) {
            handleFiles(e.target.files);
        }
    };

    const handleClickUpload = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        try {
            return new Date(dateStr).toLocaleDateString('fr-FR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
            });
        } catch (e) {
            return '-';
        }
    };

    const formatCapital = (capital) => {
        if (capital == null || capital === '0.00') return '-';
        return Number(capital).toLocaleString('fr-FR') + ' XOF';
    };

    return (
        <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
            {/* En-tête */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
                marginBottom: '24px',
                backgroundColor: '#FFF7ED',
                borderRadius: '12px',
                padding: '24px'
            }}>
                <div style={{
                    width: '56px',
                    height: '56px',
                    backgroundColor: '#F97316',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                }}>
                    <Icons.FileText size={28} color="white" />
                </div>
                <div>
                    <h1 style={{
                        fontSize: '28px',
                        fontWeight: '700',
                        color: '#F97316',
                        margin: 0
                    }}>
                        Annonces Légales
                    </h1>
                    <p style={{
                        fontSize: '14px',
                        color: '#6B7280',
                        margin: '4px 0 0 0'
                    }}>
                        Téléversez des images ou PDF d'annonces légales pour extraction automatique via OCR
                    </p>
                </div>
            </div>

            {/* Zone de téléversement */}
            <div style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                padding: '24px',
                marginBottom: '24px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
            }}>
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginBottom: '20px'
                }}>
                    <Icons.Upload size={20} color="#F97316" />
                    <h2 style={{
                        fontSize: '18px',
                        fontWeight: '600',
                        color: '#1F2937',
                        margin: 0
                    }}>
                        Zone de téléversement
                    </h2>
                </div>

                <div
                    onClick={handleClickUpload}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    style={{
                        border: '2px dashed ' + (isDragOver ? '#F97316' : '#FDBA74'),
                        borderRadius: '12px',
                        padding: '48px 24px',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        backgroundColor: isDragOver ? '#FFF7ED' : '#FFFBF5',
                        transition: 'all 0.2s ease'
                    }}
                >
                    {uploading ? (
                        <React.Fragment>
                            <div style={{
                                width: '56px',
                                height: '56px',
                                borderRadius: '50%',
                                backgroundColor: '#FFF7ED',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                marginBottom: '16px',
                                animation: 'annonce-pulse 1.5s infinite'
                            }}>
                                <Icons.Loader size={28} color="#F97316" style={{ animation: 'annonce-spin 1s linear infinite' }} />
                            </div>
                            <p style={{ fontSize: '16px', fontWeight: '500', color: '#374151', margin: 0 }}>
                                Traitement en cours...
                            </p>
                        </React.Fragment>
                    ) : (
                        <React.Fragment>
                            <div style={{
                                width: '56px',
                                height: '56px',
                                borderRadius: '50%',
                                backgroundColor: '#FFF7ED',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                marginBottom: '16px'
                            }}>
                                <Icons.Upload size={28} color="#F97316" />
                            </div>
                            <p style={{
                                fontSize: '16px',
                                fontWeight: '500',
                                color: '#374151',
                                margin: '0 0 8px 0'
                            }}>
                                Glissez vos fichiers ici ou cliquez pour sélectionner
                            </p>
                            <p style={{
                                fontSize: '13px',
                                color: '#9CA3AF',
                                margin: 0
                            }}>
                                Formats acceptés : PDF, JPG, PNG, WEBP - Taille max : 100 Mo
                            </p>
                        </React.Fragment>
                    )}
                </div>

                <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept=".pdf,.jpg,.jpeg,.png,.webp"
                    onChange={handleFileSelect}
                    style={{ display: 'none' }}
                />
            </div>

            {/* Tableau des annonces extraites */}
            <div style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                overflow: 'hidden',
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
            }}>
                {/* En-tête du tableau */}
                <div style={{
                    backgroundColor: '#FFF7ED',
                    padding: '20px 24px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px'
                }}>
                    <div style={{
                        width: '40px',
                        height: '40px',
                        backgroundColor: '#FDBA74',
                        borderRadius: '10px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                    }}>
                        <Icons.FileText size={20} color="white" />
                    </div>
                    <div>
                        <h2 style={{
                            fontSize: '18px',
                            fontWeight: '600',
                            color: '#1F2937',
                            margin: 0
                        }}>
                            Annonces Légales Extraites
                        </h2>
                        <p style={{
                            fontSize: '13px',
                            color: '#6B7280',
                            margin: '2px 0 0 0'
                        }}>
                            {totalCount} annonce{totalCount !== 1 ? 's' : ''} extraite{totalCount !== 1 ? 's' : ''}
                        </p>
                    </div>
                </div>

                {/* Table */}
                <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                        <thead>
                            <tr style={{ backgroundColor: '#FFF7ED' }}>
                                <th style={{ ...thStyle, width: '90px' }}>Date</th>
                                <th style={{ ...thStyle, width: '18%' }}>Raison Sociale</th>
                                <th style={{ ...thStyle, width: '12%' }}>Forme Jur.</th>
                                <th style={{ ...thStyle, width: '16%' }}>Siège Social</th>
                                <th style={{ ...thStyle, width: '16%' }}>Type</th>
                                <th style={{ ...thStyle, width: '12%' }}>Capital</th>
                                <th style={{ ...thStyle, width: '80px' }}>Statut</th>
                                <th style={{ ...thStyle, width: '75px' }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan="8" style={{ padding: '16px' }}>
                                        <Skeleton count={5} height={32} style={{ marginBottom: '8px' }} />
                                    </td>
                                </tr>
                            ) : annonces.length === 0 ? (
                                <tr>
                                    <td colSpan="8" style={{ padding: '60px 24px', textAlign: 'center' }}>
                                        <div style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            gap: '12px'
                                        }}>
                                            <div style={{
                                                width: '56px',
                                                height: '56px',
                                                borderRadius: '50%',
                                                backgroundColor: '#F3F4F6',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}>
                                                <Icons.FileText size={28} color="#9CA3AF" />
                                            </div>
                                            <p style={{
                                                fontSize: '16px',
                                                fontWeight: '500',
                                                color: '#6B7280',
                                                margin: 0
                                            }}>
                                                Aucune annonce extraite
                                            </p>
                                            <p style={{
                                                fontSize: '13px',
                                                color: '#9CA3AF',
                                                margin: 0
                                            }}>
                                                Téléversez des fichiers d'annonces légales pour commencer l'extraction
                                            </p>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                annonces.map(function(annonce) {
                                    var categorie = annonce.type_annonce ? annonce.type_annonce.categorie : null;
                                    return (
                                        <tr key={annonce.id} style={{
                                            borderBottom: '1px solid #F3F4F6',
                                        }}>
                                            <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>
                                                {formatDate(annonce.date_publication || annonce.date_creation)}
                                            </td>
                                            <td style={{ ...tdStyle, fontWeight: '500', maxWidth: '180px' }}>
                                                <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={annonce.personne_morale_denomination}>
                                                    {annonce.personne_morale_denomination || '-'}
                                                </div>
                                            </td>
                                            <td style={{ ...tdStyle, maxWidth: '120px' }}>
                                                <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={annonce.personne_morale_forme_juridique}>
                                                    {annonce.personne_morale_forme_juridique || '-'}
                                                </div>
                                            </td>
                                            <td style={{ ...tdStyle, maxWidth: '160px' }}>
                                                <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={annonce.personne_morale_siege_social}>
                                                    {annonce.personne_morale_siege_social || '-'}
                                                </div>
                                            </td>
                                            <td style={{ ...tdStyle, maxWidth: '150px' }}>
                                                <span title={annonce.type_annonce ? annonce.type_annonce.libelle : ''} style={{
                                                    padding: '2px 8px',
                                                    borderRadius: '4px',
                                                    fontSize: '12px',
                                                    fontWeight: '500',
                                                    display: 'inline-block',
                                                    maxWidth: '100%',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                    backgroundColor: getTypeBg(categorie),
                                                    color: getTypeColor(categorie),
                                                }}>
                                                    {annonce.type_annonce ? annonce.type_annonce.libelle : '-'}
                                                </span>
                                            </td>
                                            <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>
                                                {formatCapital(annonce.personne_morale_capital_social)}
                                            </td>
                                            <td style={tdStyle}>
                                                <span style={{
                                                    padding: '2px 8px',
                                                    borderRadius: '4px',
                                                    fontSize: '12px',
                                                    fontWeight: '500',
                                                    whiteSpace: 'nowrap',
                                                    backgroundColor: getStatutBg(annonce.statut_extraction),
                                                    color: getStatutColor(annonce.statut_extraction),
                                                }}>
                                                    {annonce.statut_extraction || '-'}
                                                </span>
                                            </td>
                                            <td style={tdStyle}>
                                                <button
                                                    onClick={function() { setSelectedAnnonce(annonce); }}
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '4px',
                                                        padding: '6px 12px',
                                                        borderRadius: '6px',
                                                        border: '1px solid #F97316',
                                                        backgroundColor: 'white',
                                                        color: '#F97316',
                                                        cursor: 'pointer',
                                                        fontSize: '13px',
                                                        fontWeight: '500',
                                                        whiteSpace: 'nowrap'
                                                    }}
                                                >
                                                    <Icons.Eye size={14} />
                                                    Voir
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '16px 24px',
                        borderTop: '1px solid #F3F4F6'
                    }}>
                        <p style={{ fontSize: '13px', color: '#6B7280', margin: 0 }}>
                            Page {page} sur {totalPages} ({totalCount} résultats)
                        </p>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <button
                                onClick={function() { handlePageChange(previousPageUrl, page - 1); }}
                                disabled={!previousPageUrl || loading}
                                style={paginationBtnStyle(!previousPageUrl)}
                            >
                                <Icons.ChevronLeft size={16} />
                                Précédent
                            </button>
                            <div style={{
                                padding: '8px 16px',
                                borderRadius: '8px',
                                backgroundColor: '#F97316',
                                color: 'white',
                                fontSize: '14px',
                                fontWeight: '600',
                                minWidth: '40px',
                                textAlign: 'center'
                            }}>
                                {page}
                            </div>
                            <button
                                onClick={function() { handlePageChange(nextPageUrl, page + 1); }}
                                disabled={!nextPageUrl || loading}
                                style={paginationBtnStyle(!nextPageUrl)}
                            >
                                Suivant
                                <Icons.ChevronRight size={16} />
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Modal de traitement */}
            {taskStatus && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 9999,
                    padding: '24px'
                }}>
                    <div style={{
                        backgroundColor: 'white',
                        borderRadius: '16px',
                        width: '100%',
                        maxWidth: '440px',
                        padding: '40px 32px',
                        textAlign: 'center',
                        boxShadow: '0 25px 50px rgba(0,0,0,0.25)'
                    }}>
                        {taskStatus.status === 'SUCCESS' ? (
                            <React.Fragment>
                                <div style={{
                                    width: '72px',
                                    height: '72px',
                                    borderRadius: '50%',
                                    backgroundColor: '#DEF7EC',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    margin: '0 auto 24px auto'
                                }}>
                                    <Icons.CheckCircle size={36} color="#03543F" />
                                </div>
                                <h3 style={{
                                    fontSize: '20px',
                                    fontWeight: '700',
                                    color: '#03543F',
                                    margin: '0 0 12px 0'
                                }}>
                                    Extraction terminée
                                </h3>
                                <p style={{
                                    fontSize: '16px',
                                    color: '#374151',
                                    margin: '0 0 16px 0',
                                    lineHeight: '1.5'
                                }}>
                                    {taskStatus.message}
                                </p>
                                <button
                                    onClick={function() { setTaskStatus(null); }}
                                    style={{
                                        padding: '10px 32px',
                                        borderRadius: '8px',
                                        border: 'none',
                                        backgroundColor: '#03543F',
                                        color: 'white',
                                        cursor: 'pointer',
                                        fontSize: '14px',
                                        fontWeight: '600',
                                        marginTop: '8px'
                                    }}
                                >
                                    Fermer
                                </button>
                            </React.Fragment>
                        ) : taskStatus.status === 'FAILURE' ? (
                            <React.Fragment>
                                <div style={{
                                    width: '72px',
                                    height: '72px',
                                    borderRadius: '50%',
                                    backgroundColor: '#FEE2E2',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    margin: '0 auto 24px auto'
                                }}>
                                    <Icons.XCircle size={36} color="#991B1B" />
                                </div>
                                <h3 style={{
                                    fontSize: '20px',
                                    fontWeight: '700',
                                    color: '#991B1B',
                                    margin: '0 0 12px 0'
                                }}>
                                    Échec du traitement
                                </h3>
                                <p style={{
                                    fontSize: '15px',
                                    color: '#374151',
                                    margin: '0 0 16px 0',
                                    lineHeight: '1.5'
                                }}>
                                    {taskStatus.message}
                                </p>
                                <button
                                    onClick={function() { setTaskStatus(null); }}
                                    style={{
                                        padding: '10px 32px',
                                        borderRadius: '8px',
                                        border: 'none',
                                        backgroundColor: '#991B1B',
                                        color: 'white',
                                        cursor: 'pointer',
                                        fontSize: '14px',
                                        fontWeight: '600',
                                        marginTop: '8px'
                                    }}
                                >
                                    Fermer
                                </button>
                            </React.Fragment>
                        ) : (
                            <React.Fragment>
                                <div style={{
                                    width: '72px',
                                    height: '72px',
                                    borderRadius: '50%',
                                    backgroundColor: '#FFF7ED',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    margin: '0 auto 24px auto'
                                }}>
                                    <Icons.Loader size={36} color="#F97316" style={{ animation: 'annonce-spin 1s linear infinite' }} />
                                </div>
                                <h3 style={{
                                    fontSize: '20px',
                                    fontWeight: '700',
                                    color: '#1F2937',
                                    margin: '0 0 8px 0'
                                }}>
                                    Traitement en cours
                                </h3>
                                <p style={{
                                    fontSize: '15px',
                                    color: '#6B7280',
                                    margin: '0 0 24px 0',
                                    lineHeight: '1.5'
                                }}>
                                    {taskStatus.message}
                                </p>
                                <p style={{
                                    fontSize: '13px',
                                    color: '#9CA3AF',
                                    margin: '0 0 16px 0',
                                    fontStyle: 'italic'
                                }}>
                                    Cela peut prendre du temps, veuillez patienter...
                                </p>
                                <div style={{
                                    display: 'inline-block',
                                    padding: '6px 16px',
                                    borderRadius: '20px',
                                    backgroundColor: getTaskStatusBg(taskStatus.status),
                                    color: getTaskStatusColor(taskStatus.status),
                                    fontSize: '12px',
                                    fontWeight: '600',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px'
                                }}>
                                    {taskStatus.status}
                                </div>
                            </React.Fragment>
                        )}
                    </div>
                </div>
            )}

            {/* Modal détail annonce */}
            {selectedAnnonce && (
                <div
                    onClick={function(e) { if (e.target === e.currentTarget) setSelectedAnnonce(null); }}
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0,0,0,0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 9999,
                        padding: '24px'
                    }}
                >
                    <div style={{
                        backgroundColor: '#F9FAFB',
                        borderRadius: '16px',
                        width: '100%',
                        maxWidth: '780px',
                        maxHeight: '90vh',
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column',
                        boxShadow: '0 25px 50px rgba(0,0,0,0.25)'
                    }}>
                        {/* Header orange */}
                        <div style={{
                            background: 'linear-gradient(135deg, #F97316, #EA580C)',
                            padding: '24px',
                            position: 'relative'
                        }}>
                            <button
                                onClick={function() { setSelectedAnnonce(null); }}
                                style={{
                                    position: 'absolute',
                                    top: '16px',
                                    right: '16px',
                                    width: '32px',
                                    height: '32px',
                                    borderRadius: '50%',
                                    border: '2px solid white',
                                    backgroundColor: 'transparent',
                                    color: 'white',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                            >
                                <Icons.X size={16} />
                            </button>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                                <span style={{
                                    padding: '4px 14px',
                                    borderRadius: '20px',
                                    fontSize: '12px',
                                    fontWeight: '600',
                                    backgroundColor: '#3B82F6',
                                    color: 'white',
                                    textTransform: 'uppercase'
                                }}>
                                    {selectedAnnonce.type_annonce ? selectedAnnonce.type_annonce.libelle : '-'}
                                </span>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: 'white', fontSize: '14px' }}>
                                    <Icons.Calendar size={14} />
                                    {formatDate(selectedAnnonce.date_publication || selectedAnnonce.date_creation)}
                                </div>
                            </div>
                            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: 'white', lineHeight: '1.4' }}>
                                {(selectedAnnonce.type_annonce ? selectedAnnonce.type_annonce.libelle : 'Annonce') + " - " + (selectedAnnonce.personne_morale_denomination || '')}
                            </h3>
                        </div>

                        {/* Body scrollable */}
                        <div style={{ padding: '24px', overflowY: 'auto', flex: 1 }}>
                            {/* Adresse */}
                            {selectedAnnonce.personne_morale_siege_social && (
                                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginBottom: '20px', color: '#EA580C', fontSize: '14px', fontWeight: '600' }}>
                                    <Icons.MapPin size={16} style={{ marginTop: '2px', flexShrink: 0 }} />
                                    {selectedAnnonce.personne_morale_siege_social}
                                </div>
                            )}

                            {/* Informations de l'entreprise */}
                            <div style={{ backgroundColor: 'white', borderRadius: '12px', border: '1px solid #E5E7EB', padding: '20px', marginBottom: '16px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px', fontSize: '14px', fontWeight: '600', color: '#374151' }}>
                                    <Icons.Grid size={16} color="#F97316" />
                                    Informations de l'entreprise
                                </div>

                                {/* Dénomination */}
                                <div style={{ borderLeft: '3px solid #F97316', paddingLeft: '12px', marginBottom: '16px' }}>
                                    <p style={{ fontSize: '11px', fontWeight: '600', color: '#F97316', margin: '0 0 2px 0', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Dénomination</p>
                                    <p style={{ fontSize: '16px', fontWeight: '700', color: '#1F2937', margin: 0 }}>{selectedAnnonce.personne_morale_denomination || '-'}</p>
                                </div>

                                {/* Capital + Forme juridique */}
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                                    <div style={{ borderLeft: '3px solid #F97316', paddingLeft: '12px' }}>
                                        <p style={{ fontSize: '11px', fontWeight: '600', color: '#F97316', margin: '0 0 2px 0', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Capital</p>
                                        <p style={{ fontSize: '15px', fontWeight: '600', color: '#1F2937', margin: 0 }}>{formatCapital(selectedAnnonce.personne_morale_capital_social)}</p>
                                    </div>
                                    <div style={{ borderLeft: '3px solid #E5E7EB', paddingLeft: '12px' }}>
                                        <p style={{ fontSize: '11px', fontWeight: '600', color: '#6B7280', margin: '0 0 2px 0', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Forme juridique</p>
                                        <p style={{ fontSize: '15px', fontWeight: '600', color: '#1F2937', margin: 0 }}>{selectedAnnonce.personne_morale_forme_juridique || '-'}</p>
                                    </div>
                                </div>

                                {/* RCCM + N° Greffe */}
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                                    <div style={{ borderLeft: '3px solid #EAB308', paddingLeft: '12px' }}>
                                        <p style={{ fontSize: '11px', fontWeight: '600', color: '#F97316', margin: '0 0 2px 0', textTransform: 'uppercase', letterSpacing: '0.5px' }}>RCCM</p>
                                        <p style={{ fontSize: '15px', fontWeight: '600', color: '#1F2937', margin: 0 }}>{selectedAnnonce.personne_morale_rccm || 'Aucune donnée'}</p>
                                    </div>
                                    <div style={{ borderLeft: '3px solid #E5E7EB', paddingLeft: '12px' }}>
                                        <p style={{ fontSize: '11px', fontWeight: '600', color: '#6B7280', margin: '0 0 2px 0', textTransform: 'uppercase', letterSpacing: '0.5px' }}>N° Greffe</p>
                                        <p style={{ fontSize: '15px', fontWeight: '600', color: selectedAnnonce.numero_greffe ? '#1F2937' : '#F97316', margin: 0, fontStyle: selectedAnnonce.numero_greffe ? 'normal' : 'italic' }}>{selectedAnnonce.numero_greffe || 'Aucune donnée'}</p>
                                    </div>
                                </div>
                            </div>

                            {/* Siège social */}
                            <div style={{ backgroundColor: 'white', borderRadius: '12px', border: '1px solid #E5E7EB', padding: '20px', marginBottom: '16px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', fontSize: '11px', fontWeight: '600', color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                    <Icons.MapPin size={14} color="#6B7280" />
                                    Siège social
                                </div>
                                <p style={{ fontSize: '15px', fontWeight: '600', color: '#1F2937', margin: 0 }}>{selectedAnnonce.personne_morale_siege_social || '-'}</p>
                            </div>

                            {/* Demandeur */}
                            {selectedAnnonce.demandeur_noms && (
                                <div style={{ backgroundColor: 'white', borderRadius: '12px', border: '1px solid #E5E7EB', padding: '20px', marginBottom: '16px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', fontSize: '11px', fontWeight: '600', color: '#F97316', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                        <Icons.Users size={14} color="#F97316" />
                                        Demandeur
                                    </div>
                                    <p style={{ fontSize: '14px', fontWeight: '600', color: '#1F2937', margin: 0, lineHeight: '1.6' }}>{selectedAnnonce.demandeur_noms}</p>
                                </div>
                            )}

                            {/* N° Formalité + Statut */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                                <div style={{ backgroundColor: 'white', borderRadius: '12px', border: '1px solid #E5E7EB', padding: '20px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', fontSize: '11px', fontWeight: '600', color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                        <Icons.Hash size={14} color="#6B7280" />
                                        N° Formalité
                                    </div>
                                    <p style={{ fontSize: '15px', fontWeight: '600', color: '#1F2937', margin: 0 }}>{selectedAnnonce.numero_formalite || '-'}</p>
                                </div>
                                <div style={{ backgroundColor: 'white', borderRadius: '12px', border: '1px solid #E5E7EB', padding: '20px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', fontSize: '11px', fontWeight: '600', color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                        <Icons.Clock size={14} color="#6B7280" />
                                        Statut extraction
                                    </div>
                                    <p style={{ fontSize: '15px', fontWeight: '600', color: '#1F2937', margin: 0 }}>{selectedAnnonce.statut_extraction || '-'}</p>
                                </div>
                            </div>

                            {/* Contenu complet */}
                            {selectedAnnonce.contenu_complet && (
                                <div style={{ marginBottom: '16px' }}>
                                    <div style={{
                                        backgroundColor: '#F97316',
                                        borderRadius: '10px 10px 0 0',
                                        padding: '14px 20px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px'
                                    }}>
                                        <Icons.FileText size={16} color="white" />
                                        <span style={{ fontSize: '13px', fontWeight: '700', color: 'white', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Contenu complet de l'annonce</span>
                                    </div>
                                    <div style={{
                                        backgroundColor: 'white',
                                        border: '1px solid #E5E7EB',
                                        borderTop: 'none',
                                        borderRadius: '0 0 10px 10px',
                                        padding: '20px',
                                        borderLeft: '3px solid #F97316'
                                    }}>
                                        <p style={{ fontSize: '14px', color: '#374151', margin: 0, lineHeight: '1.7', whiteSpace: 'pre-wrap' }}>{selectedAnnonce.contenu_complet}</p>
                                    </div>
                                </div>
                            )}

                            {/* Message si pas de contenu */}
                            {!selectedAnnonce.contenu_complet && !selectedAnnonce.demandeur_noms && (
                                <div style={{
                                    textAlign: 'center',
                                    padding: '24px',
                                    color: '#9CA3AF',
                                    fontSize: '14px'
                                }}>
                                    Aucun contenu détaillé disponible pour cette annonce.
                                </div>
                            )}
                        </div>

                        {/* Footer modal */}
                        <div style={{
                            padding: '16px 24px',
                            borderTop: '1px solid #E5E7EB',
                            backgroundColor: 'white',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between'
                        }}>
                            <span style={{ fontSize: '13px', color: '#F97316' }}>
                                {"Réf: " + (selectedAnnonce.id ? selectedAnnonce.id.substring(0, 8) : '-')}
                            </span>
                            <button
                                onClick={function() { setSelectedAnnonce(null); }}
                                style={{
                                    padding: '8px 24px',
                                    borderRadius: '8px',
                                    border: '1px solid #E5E7EB',
                                    backgroundColor: '#F9FAFB',
                                    color: '#374151',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    fontWeight: '500'
                                }}
                            >
                                Fermer
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Animation CSS */}
            <style dangerouslySetInnerHTML={{ __html: "@keyframes annonce-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } } @keyframes annonce-pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }" }} />
        </div>
    );
};

var thStyle = {
    padding: '12px 16px',
    textAlign: 'left',
    fontSize: '13px',
    fontWeight: '600',
    color: '#374151',
    borderBottom: '1px solid #FDE8CD'
};

var tdStyle = {
    padding: '12px 16px',
    fontSize: '14px',
    color: '#374151'
};

function getTypeBg(categorie) {
    if (categorie === 'constitution') return '#DEF7EC';
    if (categorie === 'modification') return '#FEF3C7';
    if (categorie === 'procedure') return '#EDE9FE';
    if (categorie === 'vie_sociale') return '#DBEAFE';
    return '#F3F4F6';
}

function getTypeColor(categorie) {
    if (categorie === 'constitution') return '#03543F';
    if (categorie === 'modification') return '#92400E';
    if (categorie === 'procedure') return '#5B21B6';
    if (categorie === 'vie_sociale') return '#1E40AF';
    return '#374151';
}

function getStatutBg(statut) {
    if (statut === 'SUCCESS') return '#DEF7EC';
    if (statut === 'FAILURE') return '#FEE2E2';
    if (statut === 'PENDING') return '#FEF3C7';
    if (statut === 'STARTED') return '#DBEAFE';
    if (statut === 'RETRY') return '#FFF7ED';
    return '#F3F4F6';
}

function getStatutColor(statut) {
    if (statut === 'SUCCESS') return '#03543F';
    if (statut === 'FAILURE') return '#991B1B';
    if (statut === 'PENDING') return '#92400E';
    if (statut === 'STARTED') return '#1E40AF';
    if (statut === 'RETRY') return '#C2410C';
    return '#374151';
}

function getTaskStatusBg(status) {
    if (status === 'PENDING') return '#FEF3C7';
    if (status === 'STARTED') return '#DBEAFE';
    if (status === 'RETRY') return '#FFF7ED';
    return '#F3F4F6';
}

function getTaskStatusColor(status) {
    if (status === 'PENDING') return '#92400E';
    if (status === 'STARTED') return '#1E40AF';
    if (status === 'RETRY') return '#C2410C';
    return '#374151';
}

function getTaskStatusMessage(status) {
    if (status === 'PENDING') return 'En attente de traitement...';
    if (status === 'STARTED') return 'Traitement en cours...';
    if (status === 'RETRY') return 'Nouvelle tentative en cours...';
    return 'Traitement en cours...';
}

function paginationBtnStyle(disabled) {
    return {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        padding: '8px 16px',
        borderRadius: '8px',
        border: '1px solid #E5E7EB',
        backgroundColor: disabled ? '#F9FAFB' : 'white',
        color: disabled ? '#D1D5DB' : '#374151',
        cursor: disabled ? 'not-allowed' : 'pointer',
        fontSize: '14px',
        fontWeight: '500'
    };
}

export default AnnonceList;
